import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Check, Copy, Loader2, Upload } from "lucide-react";
import api, { formatApiError, mediaUrl } from "@/lib/api";
import { uploadArtwork } from "@/components/signs/useSignConfig";
import { accentPair } from "@/lib/contrast";
import { Field, Inline, Primary, Secondary, Tag, copyText, inputCls, money } from "@/components/backoffice/kit";

function Preview({ name, logo, color, cards }) {
    const { accent, on } = accentPair(color);
    return (
        <div className="border border-[#1a1a1a] bg-white" data-testid="branding-preview" style={{ "--accent": accent, "--on-accent": on }}>
            <div className="flex min-h-[56px] items-center justify-between border-b border-[#1a1a1a] px-3">
                <span className="flex items-center gap-2">{logo && <img src={mediaUrl(logo)} alt="" className="h-9 w-9 object-contain" />}<span className="text-[18px] font-semibold">{name || "Your shop"}</span></span>
                <span className="text-[14px] underline">Track · Cart (0)</span>
            </div>
            <div className="grid grid-cols-2 gap-2 p-3">
                {(cards || []).slice(0, 4).map((c) => (
                    <div key={c.key} className="border border-[#1a1a1a] p-2">
                        <div className="h-16">{c.image && <img src={mediaUrl(c.image)} alt="" className="h-full w-full object-contain" />}</div>
                        <p className="mt-1 truncate text-[14px] font-semibold">{c.title}</p>
                        <p className="text-[13px] text-[#4d4d4d]">{c.from_price != null ? `from ${money(c.from_price)}` : c.hook}</p>
                        <span className="mt-1 inline-block bg-[var(--accent)] px-2 py-1 text-[12px] font-semibold text-[var(--on-accent)]">Order</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

const DOMAIN_STATE = { none: ["grey", "no domain"], pending: ["red", "Pending"], verified: ["green", "Points here"], live: ["green", "Live"] };

/** 8.7 Branding — logo, brand color, name, web address, own domain; live preview on the right (top on phones). */
export default function BrandingScreen({ store, onSaved }) {
    const [f, setF] = useState({ name: store?.name || "", logo: store?.logo || "", color: store?.theme?.primary || "#1a1a1a", banner: store?.banner || "", tagline: store?.tagline || "" });
    const [cards, setCards] = useState([]);
    const [dom, setDom] = useState(null);
    const [host, setHost] = useState("");
    const [ownOpen, setOwnOpen] = useState(false);
    const [busy, setBusy] = useState("");
    const [err, setErr] = useState("");
    const [copied, setCopied] = useState(false);
    const file = useRef(null);
    const bannerFile = useRef(null);
    const loadDomain = useCallback(() => api.get("/store/domain").then(({ data }) => { setDom(data); setHost(data.custom_domain || ""); setOwnOpen(!!data.custom_domain); }).catch(() => setDom({})), []);
    useEffect(() => { loadDomain(); }, [loadDomain]);
    useEffect(() => { if (store?.slug) api.get("/simple/cards", { params: { store: store.slug } }).then(({ data }) => setCards(data.cards || [])).catch(() => { }); }, [store?.slug]);

    const upload = async (f0, key = "logo") => {
        if (!f0) return;
        setBusy(key);
        try { const rec = await uploadArtwork(f0); setF((s) => ({ ...s, [key]: rec.url })); } catch { toast.error("Upload failed. Try again."); } finally { setBusy(""); }
    };
    const save = async () => {
        setBusy("save"); setErr("");
        try {
            const { data } = await api.put("/stores/me/settings", { name: f.name, logo: f.logo || null, theme_primary: f.color, banner: f.banner || "", tagline: f.tagline || "" });
            toast.success("Branding saved"); onSaved?.(data.store);
        } catch (e) { setErr(formatApiError(e.response?.data?.detail)); } finally { setBusy(""); }
    };
    const check = async () => {
        setBusy("domain"); setErr("");
        try {
            const h = host.trim().toLowerCase();
            if (!h) throw new Error("Type your domain first");
            if (h !== (dom?.custom_domain || "")) await api.put("/store/domain", { custom_domain: h });
            const { data } = await api.post("/store/domain/verify");
            setDom(data); toast[data.verified ? "success" : "message"](data.note);
        } catch (e) { setErr(e.response ? formatApiError(e.response?.data?.detail) : e.message); } finally { setBusy(""); }
    };
    const state = DOMAIN_STATE[dom?.status || "none"];
    return (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(320px,440px)]" data-testid="branding-screen">
            <div className="order-first lg:order-last"><Preview name={f.name} logo={f.logo} color={f.color} cards={cards} /></div>
            <div className="grid content-start gap-4">
                <h1 className="text-[22px] font-semibold">Branding</h1>
                <Inline error={err} onRetry={() => setErr("")} testid="branding-error" />
                <Field label="Logo">
                    <div className="flex items-center gap-3">
                        <div className="h-20 w-20 border border-[#1a1a1a] bg-white">{f.logo && <img src={mediaUrl(f.logo)} alt="" className="h-full w-full object-contain" />}</div>
                        <button type="button" onClick={() => file.current?.click()} disabled={busy === "logo"} data-testid="branding-change-logo"
                            className="inline-flex min-h-[44px] items-center gap-2 border border-[#1a1a1a] bg-white px-3 text-[15px] font-medium"><Upload size={16} /> Change logo</button>
                        <input ref={file} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
                    </div>
                </Field>
                <Field label="Brand color" hint="Buttons and accents only — never fields or text.">
                    <div className="flex items-center gap-2">
                        <input type="color" value={f.color} onChange={(e) => setF({ ...f, color: e.target.value })} aria-label="Pick brand color" className="h-11 w-14 border border-[#1a1a1a] bg-white p-0" data-testid="branding-swatch" />
                        <input className={`${inputCls} w-40`} value={f.color} onChange={(e) => setF({ ...f, color: e.target.value })} data-testid="branding-hex" />
                    </div>
                </Field>
                <Field label="Shop name"><input className={inputCls} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="branding-name" /></Field>
                <Field label="Home page banner" hint="A wide photo across the top of your home page (1600 × 400 works best). Until you add one, a bold placeholder in your brand color hangs there.">
                    <div className="grid gap-2">
                        <div className="flex h-24 w-full items-center justify-center overflow-hidden border border-[#1a1a1a] bg-[#0b0b0b]" data-testid="branding-banner-preview">
                            {f.banner
                                ? <img src={mediaUrl(f.banner)} alt="" className="h-full w-full object-cover" />
                                : <span className="font-mono text-[12px] uppercase tracking-widest text-white">Placeholder · your brand color</span>}
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <button type="button" onClick={() => bannerFile.current?.click()} disabled={busy === "banner"} data-testid="branding-change-banner"
                                className="inline-flex min-h-[44px] items-center gap-2 border border-[#1a1a1a] bg-white px-3 text-[15px] font-medium"><Upload size={16} /> {f.banner ? "Change banner" : "Upload banner"}</button>
                            {f.banner && <button type="button" onClick={() => setF({ ...f, banner: "" })} className="min-h-[44px] px-3 text-[15px] underline" data-testid="branding-remove-banner">Remove</button>}
                            <input ref={bannerFile} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0], "banner")} />
                        </div>
                    </div>
                </Field>
                <Field label="Banner headline" hint="One line on the banner. Blank = “<Shop> prints it. Same-day white tees, signs and flyers.”">
                    <input className={inputCls} value={f.tagline} maxLength={90} onChange={(e) => setF({ ...f, tagline: e.target.value })} placeholder="Brooklyn's same-day shirt shop." data-testid="branding-tagline" />
                </Field>
                <Primary onClick={save} disabled={busy === "save"} testid="branding-save" full>Save</Primary>

                <section className="grid gap-2 border-t border-[#767676] pt-4" data-testid="branding-domain">
                    <h2 className="text-[18px] font-semibold">Web address</h2>
                    {dom === null ? <Loader2 className="animate-spin" /> : (
                        <>
                            <p className="flex flex-wrap items-center gap-2 text-[15px]">
                                <span className="break-all" data-testid="branding-free-url">{dom.free_url || `${window.location.origin}/stores/${store?.slug}`}</span>
                                <button onClick={async () => setCopied(await copyText(dom.free_url || `${window.location.origin}/stores/${store?.slug}`))} data-testid="branding-copy-url" className="inline-flex min-h-[44px] items-center gap-1 underline">
                                    {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy link</>}
                                </button>
                            </p>
                            {!ownOpen ? <Secondary onClick={() => setOwnOpen(true)} testid="branding-own-domain">Use my own domain</Secondary> : (
                                <div className="grid gap-2">
                                    <div className="flex gap-2">
                                        <input className={inputCls} value={host} onChange={(e) => setHost(e.target.value)} placeholder="myshop.com" data-testid="branding-domain-input" />
                                        <Secondary onClick={check} disabled={busy === "domain"} testid="branding-domain-check">Check</Secondary>
                                    </div>
                                    {dom.custom_domain && (
                                        <div className="grid gap-1 text-[14px]" data-testid="branding-domain-status">
                                            <p className="flex items-center gap-2"><Tag tone={state[0]}>{state[1]}</Tag>{dom.checked_at && <span className="text-[#4d4d4d]">checked {new Date(dom.checked_at).toLocaleString()}</span>}</p>
                                            {(dom.records || []).map((r, n) => (
                                                <p key={n} className="border border-[#767676] px-2 py-1 font-mono text-[13px]" data-testid={`branding-record-${n}`}>{r.type} · {r.name} → {r.value}</p>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </section>
            </div>
        </div>
    );
}
